import { memo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage, FormattedDate } from "react-intl";
import "./PaymentPassengerFieldset.scss";
import { PASSENGER_TYPE, AGE_RANGE } from "app/constants";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import DateSelectorInputFormik from "app/pages/.shared/DateSelector/DateSelectorInputFormik";
import PaymentIdentityFieldset from "app/pages/Booking/Payment/PaymentIdentityFieldset";
import FidelityCardNumberInputContainer from "app/pages/.shared/FidelityCardNumberInputContainer";
import MilesEarnedContainer from "app/pages/.shared/MilesEarned/MilesEarnedContainer";
import CheckboxFormik from "app/pages/.shared/form/CheckboxFormik";
import { Field, useFormikContext } from "formik";

const renderLabel = (passenger, isChildrenAgeRequired) => {
	if (!passenger) {
		return null;
	}

	const { type, birthDate, index } = passenger;
	const baseId = `payment.passengers.${type}`;

	if (type === PASSENGER_TYPE.CHILD || type === PASSENGER_TYPE.INFANT) {
		const messageIdWithBirthdate = `${baseId}.with.birthdate`;
		return isChildrenAgeRequired ? (
			<FormattedMessage
				id={messageIdWithBirthdate}
				values={{
					date: (
						<span className="payment-passenger-fieldset__birthdate-label">
							<FormattedDate
								value={birthDate}
								day="2-digit"
								month="long"
								year="numeric"
							/>
						</span>
					),
				}}
			/>
		) : (
			<FormattedMessage id={baseId} values={{ index }} />
		);
	}

	return <FormattedMessage id="payment.passengers.adult" values={{ index }} />;
};

// eslint-disable-next-line react/no-multi-comp
const PaymentPassengerFieldset = ({
	isChildrenAgeRequired,
	dateOfBirthRequired,
	showFrequentTravellerCardInput,
	passenger,
	index,
}) => {
	const { values } = useFormikContext();

	const label = renderLabel(passenger, isChildrenAgeRequired);

	const ageRangeMapping = {
		[PASSENGER_TYPE.INFANT]: AGE_RANGE.FROM_0_TO_2,
		[PASSENGER_TYPE.CHILD]: isChildrenAgeRequired
			? AGE_RANGE.FROM_2_TO_17
			: AGE_RANGE.FROM_2_TO_11,
	};

	const ageRange = ageRangeMapping[passenger?.type] || AGE_RANGE.FROM_18;

	return (
		<div className="payment-passenger-fieldset" key={index}>
			<Typography
				variant={TYPOGRAPHY_VARIANTS.LARGE}
				isBold
				className="payment-passenger-fieldset__title"
				component="h3"
				data-testid={`passenger-label-${passenger?.type}-${index}`}
			>
				{label}
			</Typography>

			{passenger?.index === 1 && passenger?.type === PASSENGER_TYPE.ADULT && (
				<Field
					component={CheckboxFormik}
					name="reuseContactInfo"
					id="reuseContactInfo"
					grouped={false}
					showIcon={true}
					disabled={
						!values?.contact?.["family-name"] ||
						!values?.contact?.["given-name"] ||
						!values?.contact?.["honorific-prefix"]
					}
				>
					<Typography variant={TYPOGRAPHY_VARIANTS.REGULAR} component="p">
						<FormattedMessage id="booking.reuse.contact.info.message" />
					</Typography>
				</Field>
			)}

			<PaymentIdentityFieldset id={`passengers[${index}]`} isRequired={true} />

			{(passenger?.type !== PASSENGER_TYPE.ADULT || dateOfBirthRequired) &&
				!(
					isChildrenAgeRequired &&
					(passenger?.type === PASSENGER_TYPE.CHILD ||
						passenger?.type === PASSENGER_TYPE.INFANT)
				) && (
					<div className="payment-passenger-fieldset__birthdate">
						<DateSelectorInputFormik
							formatMonth="short"
							name={`passengers[${index}].birthdate`}
							isRequired
							label={<FormattedMessage id="payment.pasengers.birthdate.label" />}
							isChildrenAgeRequired={isChildrenAgeRequired}
							id={`payment-passenger-child-${index}`}
							type={passenger?.type}
							rangeYear={ageRange}
							data-testid={`passenger-date-selector-${passenger?.type}-${index}`}
						/>
					</div>
				)}

			{showFrequentTravellerCardInput && (
				<div className="payment-passenger-fieldset__frequent-flyer-traveller">
					<FidelityCardNumberInputContainer id={`passengers[${index}]`} />
					<MilesEarnedContainer
						suffixLabel={<FormattedMessage id="miles.earned.suffix.to.earn" />}
					/>
				</div>
			)}
		</div>
	);
};

PaymentPassengerFieldset.propTypes = {
	isChildrenAgeRequired: PropTypes.bool,
	dateOfBirthRequired: PropTypes.bool,
	showFrequentTravellerCardInput: PropTypes.bool,
	passenger: PropTypes.shape({
		birthDate: PropTypes.instanceOf(Date).isRequired,
		type: PropTypes.string.isRequired,
		index: PropTypes.number.isRequired,
	}),
	index: PropTypes.number.isRequired,
};

export default memo(PaymentPassengerFieldset);
